/**
 * Revisar con IA — pestaña de Marcas y Modelos (fase 4 de "marcas y
 * modelos sin catálogo").
 *
 * Además de lo desconocido (que va a la IA), lista lo que la lista estándar
 * ya conoce pero está escrito distinto en los datos vivos ("X-1000" ->
 * "X1000"): esas filas vienen como "Escritura estándar", marcadas, y al
 * aceptarlas solo se corrigen las unidades (no hace falta corrección propia).
 *
 * El negocio la ejecuta cuando quiere: el backend junta las marcas/modelos
 * de sus datos vivos que ni la lista estándar compartida ni sus
 * correcciones propias reconocen, y la IA propone para cada escritura si es
 * una marca/modelo estándar mal escrito, un vehículo nuevo o algo que no es
 * vehículo. Nada se aplica solo: el negocio marca las que acepta (vienen
 * marcadas las de confianza alta), puede corregir el nombre propuesto y, al
 * aceptar, ve cuántas unidades vivas y préstamos activos cambiarán.
 *
 * Aceptar corrige sus datos vivos y guarda la corrección para sus
 * registros nuevos. Lo aceptado es SOLO de este negocio: no cambia la lista
 * estándar de los demás. Las correcciones guardadas se listan abajo y se
 * pueden quitar (quitar no revierte las unidades ya corregidas).
 */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGlobalMDAlert } from "context";
import { FP, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const LARGO = 15; // factmot.marca/modelo son varchar(15)

const TIPOS = {
  estandar: { label: "Escritura estándar", color: "success" },
  existente: { label: "Mal escrita", color: "info" },
  nuevo: { label: "Nuevo", color: "warning" },
  no_vehiculo: { label: "No es vehículo", color: "default" },
};

// Vienen marcadas las propuestas en que la IA está segura.
const preseleccionar = (p) =>
  p.tipo === "estandar" ||
  (p.tipo === "existente" && p.confianza >= 0.85) ||
  (p.tipo === "no_vehiculo" && p.confianza >= 0.9);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };

export default function RevisionIA({ puedeModificar }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [propuestas, setPropuestas] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [revisando, setRevisando] = useState(false);
  const [resumen, setResumen] = useState(null); // {total, revisadas}

  const [correcciones, setCorrecciones] = useState([]);
  const cargarCorrecciones = async () => {
    const { ok, data } = await post("/api/marcasmodelos_locales", {});
    if (ok) setCorrecciones(data.correcciones || []);
  };
  useEffect(() => {
    cargarCorrecciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revisar = async () => {
    setRevisando(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_ia_revisar", {});
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo revisar con IA", color: "error" });
        return;
      }
      const filas = (data.propuestas || []).map((p, i) => ({ ...p, id: i }));
      setPropuestas(filas);
      setSeleccion(filas.filter(preseleccionar).map((p) => p.id));
      setResumen({
        total: data.total || 0,
        revisadas: data.revisadas || 0,
        estandar: data.estandar || 0,
      });
    } finally {
      setRevisando(false);
    }
  };

  const editarFila = (fila) => {
    const limpia = {
      ...fila,
      marca: String(fila.marca || "")
        .toUpperCase()
        .slice(0, LARGO),
      modelo: String(fila.modelo || "")
        .toUpperCase()
        .slice(0, LARGO),
    };
    setPropuestas((prev) => prev.map((p) => (p.id === limpia.id ? limpia : p)));
    return limpia;
  };

  // ---- Aceptar: primero el conteo, después confirmar ----
  const [confirmacion, setConfirmacion] = useState(null); // {unidades, prestamos, correcciones}
  const [aplicando, setAplicando] = useState(false);
  const elegidas = useMemo(
    () => propuestas.filter((p) => seleccion.includes(p.id)),
    [propuestas, seleccion]
  );
  const cuerpo = () => ({
    items: elegidas.map((p) => ({
      marca_escrita: p.marca_escrita,
      modelo_escrito: p.modelo_escrito,
      tipo: p.tipo,
      marca: p.marca,
      modelo: p.modelo,
    })),
  });

  const pedirConteo = async () => {
    const { ok, data } = await post("/api/marcasmodelos_ia_aceptar", cuerpo());
    if (ok) setConfirmacion(data);
    else showAlert({ message: data.Error || "No se pudo consultar el alcance", color: "error" });
  };

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_ia_aceptar", {
        ...cuerpo(),
        confirmar: true,
      });
      if (!ok) {
        showAlert({
          message: data.Error || "No se pudieron aplicar las correcciones",
          color: "error",
        });
        return;
      }
      showAlert({ message: data.ok || "Correcciones aplicadas", color: "success" });
      const hechas = new Set(seleccion);
      setPropuestas((prev) => prev.filter((p) => !hechas.has(p.id)));
      setSeleccion([]);
      setConfirmacion(null);
      cargarCorrecciones();
    } finally {
      setAplicando(false);
    }
  };

  const quitar = async (fila) => {
    const { ok, data } = await post("/api/marcasmodelos_local_eliminar", { id: fila.id });
    if (ok) cargarCorrecciones();
    else showAlert({ message: data.Error || "No se pudo quitar la corrección", color: "error" });
  };

  const columnas = [
    {
      field: "escrito",
      headerName: "Escrito en el negocio",
      flex: 1.2,
      minWidth: 180,
      valueGetter: (_, row) => `${row.marca_escrita} · ${row.modelo_escrito || "(sin modelo)"}`,
    },
    {
      field: "uso",
      headerName: "Unid. / Prést.",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: (_, row) => `${row.unidades} / ${row.prestamos}`,
      sortable: false,
    },
    { field: "marca", headerName: "Marca propuesta", flex: 0.8, minWidth: 130, editable: true },
    { field: "modelo", headerName: "Modelo propuesto", flex: 0.9, minWidth: 140, editable: true },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 130,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={TIPOS[value]?.label || value}
          color={TIPOS[value]?.color || "default"}
        />
      ),
    },
    {
      field: "confianza",
      headerName: "Confianza",
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value, row }) => (
        <Tooltip title={row.nota || ""} placement="left">
          <span>{Math.round((value || 0) * 100)}%</span>
        </Tooltip>
      ),
    },
  ];

  const columnasCorrecciones = [
    { field: "escrito", headerName: "Escrito", flex: 1, minWidth: 160 },
    {
      field: "destino",
      headerName: "Se guarda como",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) =>
        row.origen === "novehiculo"
          ? "Tal cual (no es vehículo)"
          : `${row.marca} ${row.modelo}`.trim(),
    },
    { field: "usuario", headerName: "Usuario", width: 120 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "acciones",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (params) =>
        puedeModificar && (
          <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={() => quitar(params.row)}>
            Quitar
          </Button>
        ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <Typography sx={{ flex: 1, fontSize: "0.85rem", color: FP.color.textSecondary }}>
          La IA revisa las marcas y modelos de sus unidades disponibles y préstamos activos que no
          coinciden con la lista estándar y propone cómo corregirlos. Nada cambia hasta que acepte.
          Lo que acepte aplica solo a este negocio.
        </Typography>
        {puedeModificar && (
          <Button
            onClick={revisar}
            disabled={revisando}
            startIcon={
              revisando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoFixHighOutlinedIcon />
              )
            }
            sx={{
              ...btnBase,
              bgcolor: FP.color.navy,
              color: "#fff",
              "&:hover": { bgcolor: FP.color.navySoft },
            }}
          >
            {revisando ? "Revisando…" : "Revisar con IA"}
          </Button>
        )}
      </Stack>

      {resumen && (
        <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}>
          {resumen.total === 0 && resumen.estandar === 0
            ? "Todo lo vivo coincide con la lista estándar o con sus correcciones."
            : [
                resumen.estandar > 0 &&
                  `${resumen.estandar} ya están en la lista estándar pero escritas distinto (vienen marcadas)`,
                resumen.total > 0 &&
                  `${resumen.revisadas} de ${
                    resumen.total
                  } escrituras desconocidas revisadas con IA${
                    resumen.total > resumen.revisadas
                      ? " (las de más unidades; vuelva a revisar para ver las demás)"
                      : ""
                  }`,
              ]
                .filter(Boolean)
                .join(". ")
                .concat(". Doble clic en la propuesta para corregirla.")}
        </Typography>
      )}

      {propuestas.length > 0 && (
        <>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={propuestas}
              columns={columnas}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={seleccion}
              onRowSelectionModelChange={(m) => setSeleccion(m)}
              processRowUpdate={editarFila}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              rowHeight={44}
              sx={fpDataGridSx}
            />
          </Box>
          {puedeModificar && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                onClick={pedirConteo}
                disabled={!elegidas.length}
                startIcon={<DoneAllIcon />}
                sx={{
                  ...btnBase,
                  bgcolor: FP.color.navy,
                  color: "#fff",
                  "&:hover": { bgcolor: FP.color.navySoft },
                }}
              >
                Aceptar seleccionadas ({elegidas.length})
              </Button>
            </Stack>
          )}
        </>
      )}

      <Box>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Correcciones de este negocio</Typography>
        <Box sx={{ bgcolor: "#fff" }}>
          <DataGrid
            autoHeight
            rows={correcciones}
            columns={columnasCorrecciones}
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            rowHeight={40}
            localeText={{ noRowsLabel: "Todavía no hay correcciones propias" }}
            sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "120px" }}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>

      <Dialog
        open={confirmacion !== null}
        onClose={() => setConfirmacion(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Aceptar correcciones</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se actualizarán <b>{confirmacion?.unidades || 0}</b> unidades del inventario y{" "}
            <b>{confirmacion?.prestamos || 0}</b> préstamos activos, y se guardarán{" "}
            <b>{confirmacion?.correcciones || 0}</b> correcciones para los registros nuevos de este
            negocio. Lo vendido, incautado o en tránsito y el histórico facturado no se tocan.
            {(confirmacion?.catalogo?.nuevos || 0) + (confirmacion?.catalogo?.renombrados || 0) >
              0 && (
              <>
                {" "}
                Al catálogo: <b>{confirmacion.catalogo.nuevos}</b> modelos nuevos y{" "}
                <b>{confirmacion.catalogo.renombrados}</b> renombrados (conservan su precio), para
                ponerles precio desde la pestaña Catálogo.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmacion(null)} sx={btnBase}>
            Cancelar
          </Button>
          <Button
            onClick={aplicar}
            disabled={aplicando}
            startIcon={aplicando ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
            sx={{
              ...btnBase,
              bgcolor: FP.color.navy,
              color: "#fff",
              "&:hover": { bgcolor: FP.color.navySoft },
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

RevisionIA.propTypes = {
  puedeModificar: PropTypes.bool.isRequired,
};
